import type { AttachmentDto } from '@/api/attachment';
import { http } from '@/api/http';
import type { OperationResult, Schemas } from '@/api/shared';
import type { QuestionnaireDelivery } from '@/features/patient-record/types';

/**
 * Create/regenerate response: the Phase-1 Questionnaire plus the one-time reveal fields the
 * PTV2-1040 Phase-2 backend returns for a URL delivery (present only on these responses; not in
 * the generated schema, so declared by hand).
 */
export type QuestionnaireCreatedDto = Schemas['Questionnaire'] & {
  readonly access_token?: string | null;
  readonly questionnaire_uri?: string | null;
};

/**
 * Wire DTO for a delivery_record attempt (PTV2-1162). Hand-written (not yet in the generated
 * schema); the mapper normalises to {@link DeliveryRecordViewModel}.
 */
export interface DeliveryRecordDto {
  readonly id?: string;
  readonly channel?: string | null;
  readonly recipient?: string | null;
  readonly status?: string | null;
  readonly error_code?: string | null;
  readonly error_message?: string | null;
  readonly attempt_no?: number | null;
  readonly requested_at?: string | null;
  readonly sent_at?: string | null;
  readonly status_updated_at?: string | null;
}

export const questionnairesApi = {
  /** GET /persons/{guid}/questionnaires — PPQ assignments. */
  getQuestionnaires: (
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<OperationResult<'getQuestionnaires'>> =>
    http.get<OperationResult<'getQuestionnaires'>>(
      `/persons/${encodeURIComponent(guid)}/questionnaires`,
      options,
    ),

  /** POST /persons/{guid}/questionnaires — send a questionnaire to the patient. */
  sendQuestionnaire: (
    guid: string,
    // Hand-written body: the generated schema still types delivery_method as EMAIL/SMS/PORTAL;
    // the PTV2-1040 backend uses EMAIL/SMS/URL. Decoupled until schema.ts is regenerated.
    body: { template_id: string; delivery_method: QuestionnaireDelivery; expires_at?: string | null },
    options?: { signal?: AbortSignal },
  ): Promise<QuestionnaireCreatedDto> =>
    http.post<QuestionnaireCreatedDto>(
      `/persons/${encodeURIComponent(guid)}/questionnaires`,
      body,
      options,
    ),

  /**
   * POST /persons/{guid}/questionnaires/{id}/regenerate-link — rotate a URL questionnaire's access
   * token; the previous link dies. Returns the new access_token + questionnaire_uri once.
   */
  regenerateQuestionnaireLink: (
    guid: string,
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<QuestionnaireCreatedDto> =>
    http.post<QuestionnaireCreatedDto>(
      `/persons/${encodeURIComponent(guid)}/questionnaires/${encodeURIComponent(id)}/regenerate-link`,
      undefined,
      options,
    ),

  /** GET /persons/{guid}/questionnaires/{id}/deliveries — delivery ("push") attempts for a request. */
  getQuestionnaireDeliveries: (
    guid: string,
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<DeliveryRecordDto[]> =>
    http.get<DeliveryRecordDto[]>(
      `/persons/${encodeURIComponent(guid)}/questionnaires/${encodeURIComponent(id)}/deliveries`,
      options,
    ),

  /** GET /persons/{guid}/questionnaires/{id}/attachments — submitted PDF/JSON for a request. */
  getQuestionnaireAttachments: (
    guid: string,
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<AttachmentDto[]> =>
    http.get<AttachmentDto[]>(
      `/persons/${encodeURIComponent(guid)}/questionnaires/${encodeURIComponent(id)}/attachments`,
      options,
    ),

  /** PUT /persons/{guid}/questionnaires/{id} — revoke or expire an assignment. */
  updateQuestionnaire: (
    guid: string,
    id: string,
    body: { status: 'SENT' | 'REVOKED' | 'EXPIRED' },
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.put<undefined>(
      `/persons/${encodeURIComponent(guid)}/questionnaires/${encodeURIComponent(id)}`,
      body,
      options,
    ),
};
